// =========================================================================
// calendarService.js — Google Calendar Integration (Auto-sync + 2-way)
// =========================================================================

import { isUpcomingAccra } from '../utils/firmassistTime';

const GOOGLE_CLIENT_ID = '617176814381-f2g9pd0qv92vrvsujchp6ig9vrl9ob6h.apps.googleusercontent.com';
const GOOGLE_API_KEY = 'AIzaSyAWD6s-262p7iYeWyrxrD6FTfpATkt6YN0';
const SCOPES = 'https://www.googleapis.com/auth/calendar.events';
const DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest';

let tokenClient = null;
// eslint-disable-next-line no-unused-vars
let gapiInited = false;
let gisInited = false;

// ── Load Google API scripts ──────────────────────────────────────────────
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export async function initGoogleCalendar() {
  try {
    // Load GAPI script
    await loadScript('https://apis.google.com/js/api.js');
    
    // Wait for window.gapi to be available
    await new Promise((resolve, reject) => {
      let attempts = 0;
      const check = () => {
        if (window.gapi) { resolve(); return; }
        attempts++;
        if (attempts > 50) { reject(new Error('gapi failed to load')); return; }
        setTimeout(check, 100);
      };
      check();
    });

    // Load the client library
    await new Promise((resolve, reject) => {
      window.gapi.load('client', { callback: resolve, onerror: reject });
    });

    // Init the client
    await window.gapi.client.init({
      apiKey: GOOGLE_API_KEY,
      discoveryDocs: [DISCOVERY_DOC],
    });
    gapiInited = true;

    // Load GIS (Google Identity Services)
    await loadScript('https://accounts.google.com/gsi/client');
    
    // Wait for window.google to be available
    await new Promise((resolve, reject) => {
      let attempts = 0;
      const check = () => {
        if (window.google?.accounts?.oauth2) { resolve(); return; }
        attempts++;
        if (attempts > 50) { reject(new Error('Google Identity Services failed to load')); return; }
        setTimeout(check, 100);
      };
      check();
    });

    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: SCOPES,
      callback: '',
      ux_mode: 'popup',
    });
    gisInited = true;

    return { success: true };
  } catch (error) {
    console.error('Google Calendar init error:', error);
    return { success: false, error: error.message };
  }
}

// ── Auth ─────────────────────────────────────────────────────────────────
export function googleCalendarSignIn() {
  return new Promise((resolve, reject) => {
    if (!gisInited || !tokenClient) {
      reject(new Error('Google Calendar not initialized. Please refresh the page and try again.'));
      return;
    }

    // Check for existing valid token first
    const existingToken = window.gapi?.client?.getToken();
    if (existingToken?.access_token) {
      resolve({ success: true, token: existingToken.access_token });
      return;
    }

    tokenClient.callback = (response) => {
      if (response.error) {
        console.error('Google Calendar auth error:', response);
        if (response.error === 'popup_blocked_by_browser') {
          reject(new Error('Pop-up blocked. Please allow pop-ups for this site and try again.'));
        } else if (response.error === 'access_denied') {
          reject(new Error('Access denied. Please grant calendar permissions when prompted.'));
        } else {
          reject(new Error('Google sign-in error: ' + response.error));
        }
        return;
      }
      // Persist token
      const tokenData = {
        access_token: response.access_token,
        expires_at: Date.now() + (response.expires_in || 3600) * 1000
      };
      const storageKey = currentUserId ? `gcal_token_${currentUserId}` : 'gcal_token';
      localStorage.setItem(storageKey, JSON.stringify(tokenData));
      resolve({ success: true, token: response.access_token });
    };
    tokenClient.error_callback = (error) => {
      console.error('Google Calendar error_callback:', error);
      const msg = error?.message || error?.type || 'Unknown error';
      if (msg.includes('popup_closed') || msg.includes('popup_failed_to_open')) {
        reject(new Error('Pop-up was closed or blocked. Please allow pop-ups for this site, then try again.'));
      } else {
        reject(new Error('Google Calendar connection failed: ' + msg));
      }
    };
    try {
      tokenClient.requestAccessToken({ prompt: 'consent' });
    } catch (e) {
      console.error('requestAccessToken error:', e);
      reject(new Error('Failed to open Google sign-in. Please allow pop-ups and try again.'));
    }
  });
}

export function googleCalendarSignOut() {
  const token = window.gapi?.client?.getToken();
  if (token) {
    window.google.accounts.oauth2.revoke(token.access_token);
    window.gapi.client.setToken('');
  }
  const storageKey = currentUserId ? `gcal_token_${currentUserId}` : 'gcal_token';
  localStorage.removeItem(storageKey);
  return { success: true };
}

export function isGoogleCalendarConnected() {
  return window.gapi?.client?.getToken() !== null;
}

// Track which user is currently logged in
let currentUserId = null;
export function setCurrentUserId(uid) {
  currentUserId = uid;
}

/**
 * Restore token from localStorage for the current user.
 */
export function restoreGoogleCalendarToken() {
  try {
    const storageKey = currentUserId ? `gcal_token_${currentUserId}` : 'gcal_token';
    const stored = localStorage.getItem(storageKey);
    if (!stored) return false;
    const tokenData = JSON.parse(stored);
    if (tokenData.expires_at && tokenData.expires_at > Date.now() + 300000) {
      window.gapi.client.setToken({ access_token: tokenData.access_token });
      return true;
    }
    localStorage.removeItem(storageKey);
    return false;
  } catch (e) {
    return false;
  }
}

/**
 * Silently refresh the token if needed.
 */
export function refreshGoogleCalendarToken() {
  return new Promise((resolve) => {
    if (!gisInited || !tokenClient) { resolve(false); return; }
    tokenClient.callback = (response) => {
      if (response.error) { resolve(false); return; }
      const tokenData = {
        access_token: response.access_token,
        expires_at: Date.now() + (response.expires_in || 3600) * 1000
      };
      const storageKey = currentUserId ? `gcal_token_${currentUserId}` : 'gcal_token';
      localStorage.setItem(storageKey, JSON.stringify(tokenData));
      resolve(true);
    };
    tokenClient.error_callback = () => { resolve(false); };
    tokenClient.requestAccessToken({ prompt: '' });
  });
}

// ── CRUD operations ──────────────────────────────────────────────────────
export async function getGoogleCalendarEvents(timeMin, timeMax) {
  try {
    if (!isGoogleCalendarConnected()) return { success: false, error: 'Not connected' };
    const response = await window.gapi.client.calendar.events.list({
      calendarId: 'primary',
      timeMin: timeMin || new Date().toISOString(),
      timeMax: timeMax || new Date(Date.now() + 90 * 86400000).toISOString(),
      showDeleted: false,
      singleEvents: true,
      maxResults: 250,
      orderBy: 'startTime',
    });
    return { success: true, events: response.result.items || [] };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function createGoogleCalendarEvent(event) {
  try {
    if (!isGoogleCalendarConnected()) return { success: false, error: 'Not connected' };
    const calEvent = buildGoogleEvent(event);
    console.log('Creating Google Calendar event:', JSON.stringify(calEvent));
    const response = await window.gapi.client.calendar.events.insert({
      calendarId: 'primary',
      resource: calEvent,
    });
    console.log('Google Calendar event created:', response.result.id);
    return { success: true, eventId: response.result.id, htmlLink: response.result.htmlLink };
  } catch (error) {
    const errMsg = error?.result?.error?.message || error.message || 'Unknown error';
    console.error('Failed to create Google Calendar event:', errMsg, error);
    return { success: false, error: errMsg };
  }
}

export async function updateGoogleCalendarEvent(googleEventId, event) {
  try {
    if (!isGoogleCalendarConnected()) return { success: false, error: 'Not connected' };
    const calEvent = buildGoogleEvent(event);
    const response = await window.gapi.client.calendar.events.update({
      calendarId: 'primary',
      eventId: googleEventId,
      resource: calEvent,
    });
    return { success: true, eventId: response.result.id };
  } catch (error) {
    console.error('[calendarService] updateGoogleCalendarEvent failed:', googleEventId, error);
    return { success: false, error: error.message };
  }
}

export async function deleteGoogleCalendarEvent(googleEventId) {
  try {
    if (!isGoogleCalendarConnected()) return { success: false, error: 'Not connected' };
    await window.gapi.client.calendar.events.delete({
      calendarId: 'primary',
      eventId: googleEventId,
    });
    return { success: true };
  } catch (error) {
    console.error('[calendarService] deleteGoogleCalendarEvent failed:', googleEventId, error);
    return { success: false, error: error.message };
  }
}

// ══════════════════════════════════════════════════════════════════════════
// AUTO-SYNC: Push single event to Google Calendar on creation
// ══════════════════════════════════════════════════════════════════════════

export async function autoSyncTaskToGoogleCalendar(task) {
  if (!isGoogleCalendarConnected() || !task.dueDate) return null;
  try {
    const event = {
      title: task.title || 'Task',
      date: task.dueDate,
      time: task.dueTime || null,
      location: task.location || '',
      description: [
        task.caseName ? `Case: ${task.caseName}` : '',
        task.eventType === 'court_date' ? '⚖️ Court Date' : '📋 Task',
        task.priority ? `Priority: ${task.priority}` : '',
        task.notes || '',
        '\n— Synced from FirmAssist'
      ].filter(Boolean).join('\n'),
      colorId: task.eventType === 'court_date' ? '11' : '9',
    };
    const r = await createGoogleCalendarEvent(event);
    return r.success ? r.eventId : null;
  } catch (e) {
    console.error('Auto-sync task failed:', e);
    return null;
  }
}

export async function autoSyncMeetingToGoogleCalendar(meeting) {
  if (!isGoogleCalendarConnected() || !meeting.meetingDate) return null;
  try {
    const event = {
      title: meeting.title || 'Meeting',
      date: meeting.meetingDate,
      time: meeting.meetingTime || null,
      location: meeting.location || '',
      description: [
        meeting.notes || '',
        '\n— Synced from FirmAssist'
      ].filter(Boolean).join('\n'),
      colorId: '3',
    };
    const r = await createGoogleCalendarEvent(event);
    return r.success ? r.eventId : null;
  } catch (e) {
    console.error('Auto-sync meeting failed:', e);
    return null;
  }
}

// ══════════════════════════════════════════════════════════════════════════
// BULK SYNC: Push all pending events to Google Calendar
// ══════════════════════════════════════════════════════════════════════════

export async function syncAllToGoogleCalendar(tasks, meetings) {
  if (!isGoogleCalendarConnected()) {
    return { success: false, error: 'Not connected to Google Calendar' };
  }
  let synced = 0, failed = 0;
  const results = [];
  const errors = [];

  for (const task of (tasks || [])) {
    if (task.status === 'completed' || !task.dueDate) continue;
    try {
      const event = {
        title: task.title, date: task.dueDate, time: task.dueTime || null,
        location: task.location || '',
        description: [
          task.caseName ? `Case: ${task.caseName}` : '',
          task.eventType === 'court_date' ? '⚖️ Court Date' : '📋 Task',
          task.priority ? `Priority: ${task.priority}` : '',
          '\n— Synced from FirmAssist'
        ].filter(Boolean).join('\n'),
        colorId: task.eventType === 'court_date' ? '11' : '9',
      };
      if (task.googleCalendarEventId) {
        const r = await updateGoogleCalendarEvent(task.googleCalendarEventId, event);
        if (r.success) { synced++; } else { failed++; errors.push(r.error); }
      } else {
        const r = await createGoogleCalendarEvent(event);
        if (r.success) {
          synced++;
          results.push({ taskId: task.id, googleEventId: r.eventId });
        } else {
          failed++;
          errors.push(`Task "${task.title}": ${r.error}`);
        }
      }
    } catch (e) { failed++; errors.push(e.message); }
  }

  for (const meeting of (meetings || [])) {
    if (meeting.status === 'cancelled' || !meeting.meetingDate) continue;
    try {
      const event = {
        title: meeting.title || 'Meeting', date: meeting.meetingDate,
        time: meeting.meetingTime || null, location: meeting.location || '',
        description: ['— Synced from FirmAssist'].join('\n'),
        colorId: '3',
      };
      if (meeting.googleCalendarEventId) {
        const r = await updateGoogleCalendarEvent(meeting.googleCalendarEventId, event);
        if (r.success) { synced++; } else { failed++; errors.push(r.error); }
      } else {
        const r = await createGoogleCalendarEvent(event);
        if (r.success) {
          synced++;
          results.push({ meetingId: meeting.id, googleEventId: r.eventId });
        } else {
          failed++;
          errors.push(`Meeting "${meeting.title}": ${r.error}`);
        }
      }
    } catch (e) { failed++; errors.push(e.message); }
  }

  console.log('Google Calendar sync results:', { synced, failed, errors });
  return { success: true, synced, failed, results, errors };
}

// ══════════════════════════════════════════════════════════════════════════
// 2-WAY SYNC: Pull Google Calendar events into FirmAssist format
// ══════════════════════════════════════════════════════════════════════════

export async function syncFromGoogleCalendar() {
  if (!isGoogleCalendarConnected()) {
    return { success: false, error: 'Not connected to Google Calendar' };
  }
  try {
    // Look back 7 days and forward 90 days to catch recent + upcoming events
    const pastWeek = new Date(Date.now() - 7 * 86400000);
    const threeMonths = new Date(Date.now() + 90 * 86400000);
    const result = await getGoogleCalendarEvents(pastWeek.toISOString(), threeMonths.toISOString());
    
    if (!result.success) return result;

    console.log('Google Calendar events fetched:', result.events?.length);

    // Filter out events that originated from FirmAssist
    const externalEvents = (result.events || []).filter(ev => {
      const desc = ev.description || '';
      return !desc.includes('— Synced from FirmAssist');
    });

    console.log('External events (not from FirmAssist):', externalEvents.length);

    // Convert to FirmAssist task format
    const importedTasks = externalEvents.map(ev => {
      const startDate = ev.start?.date || ev.start?.dateTime?.split('T')[0] || '';
      const startTime = ev.start?.dateTime ? formatTimeFromISO(ev.start.dateTime) : '';
      const summary = (ev.summary || '').toLowerCase();
      const isCourt = summary.includes('court') || summary.includes('hearing') ||
                      summary.includes('trial') || summary.includes('sitting');

      return {
        googleCalendarEventId: ev.id,
        title: ev.summary || 'Google Calendar Event',
        dueDate: startDate,
        dueTime: startTime,
        location: ev.location || '',
        eventType: isCourt ? 'court_date' : 'task',
        priority: 'medium',
        status: 'pending',
        notes: (ev.description || '').substring(0, 500),
        source: 'google_calendar',
        googleEventLink: ev.htmlLink || '',
      };
    });

    return { success: true, events: importedTasks, count: importedTasks.length };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// ══════════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════════

function buildGoogleEvent(event) {
  const calEvent = {
    summary: event.title,
    location: event.location || '',
    description: event.description || '',
  };
  if (event.colorId) calEvent.colorId = event.colorId;

  if (event.time) {
    try {
      const time24 = convertTo24h(event.time);
      const dateTime = `${event.date}T${time24}:00`;
      const testDate = new Date(dateTime);
      // Verify the date is valid before using it
      if (isNaN(testDate.getTime())) throw new Error('Invalid date');
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      calEvent.start = { dateTime, timeZone: tz };
      const endDate = new Date(testDate.getTime() + 3600000); // +1 hour
      calEvent.end = { dateTime: endDate.toISOString(), timeZone: tz };
    } catch (e) {
      // Fallback to all-day event if time parsing fails
      calEvent.start = { date: event.date };
      const nextDay = new Date(event.date + 'T00:00:00');
      nextDay.setDate(nextDay.getDate() + 1);
      calEvent.end = { date: nextDay.toISOString().split('T')[0] };
    }
  } else {
    calEvent.start = { date: event.date };
    const nextDay = new Date(event.date + 'T00:00:00');
    nextDay.setDate(nextDay.getDate() + 1);
    calEvent.end = { date: nextDay.toISOString().split('T')[0] };
  }

  calEvent.reminders = {
    useDefault: false,
    overrides: [
      { method: 'popup', minutes: 60 },
      { method: 'popup', minutes: 1440 },
    ],
  };
  return calEvent;
}

function convertTo24h(timeStr) {
  if (!timeStr) return '09:00';
  if (timeStr.match(/^\d{2}:\d{2}$/)) return timeStr;
  const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(am|pm|AM|PM)?/);
  if (!match) return '09:00';
  let hours = parseInt(match[1]);
  const minutes = match[2] || '00';
  const period = (match[3] || '').toLowerCase();
  if (period === 'pm' && hours < 12) hours += 12;
  if (period === 'am' && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, '0')}:${minutes}`;
}

function formatTimeFromISO(isoString) {
  try {
    const d = new Date(isoString);
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';
    if (hours > 12) hours -= 12;
    if (hours === 0) hours = 12;
    return `${hours}:${minutes} ${period}`;
  } catch (e) { return ''; }
}

// ── Dashboard helper ─────────────────────────────────────────────────────
export function getWeekEvents(cases, tasks) {
  const events = [];
  (tasks || []).forEach(t => {
    if (!t.dueDate || t.status === 'completed') return;
    // Africa/Accra business-time semantics (utils/firmassistTime.js) — the
    // viewer's own device timezone must not change which week a task falls in.
    if (isUpcomingAccra(t.dueDate, new Date(), 7)) {
      events.push({
        id: 'task-' + t.id,
        title: t.title,
        caseName: t.caseName || t.case || '',
        date: t.dueDate,
        type: t.eventType === 'court_date' ? 'case' : 'task',
        priority: t.priority || 'medium',
      });
    }
  });
  events.sort((a, b) => new Date(a.date) - new Date(b.date));
  return events;
}

export const syncToGoogleCalendar = syncAllToGoogleCalendar;
